import React, { useState, useEffect, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import * as Sentry from "@sentry/react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useSelector, useDispatch } from "react-redux";
import { analytics, db } from "../firebase-config";
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import TawkToChat from "../components/TawkToChat";
import Navbar from "../components/Navbar";
import { useAuth } from "../useAuth";
import {
  fetchAndSetCompanyData,
  selectAllCompanys,
} from "../utils/redux/companySlice";
import { fetchUserByEmail } from "../utils/redux/userDataSlice";
import convertCurrencyWithFee from "../utils/convertCurrencyWithFee";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import CinetPayButton from "../components/CinetPayButton";
import { getFunctions, httpsCallable } from "firebase/functions";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";

// **** I18NEXT IMPORT ****
import { useTranslation } from 'react-i18next';
// *************************

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

// Refactored calculation function - Takes prices as arguments
const calculateDiscountedTotal = (
    counts, // { basic: number, growth: number, pro: number, premium: number }
    prices, // { basic: number, growth: number, pro: number, premium: number }
    discount // percentage (e.g., 10 for 10%)
) => {
    if (
        prices.basic === null ||
        prices.growth === null ||
        prices.pro === null ||
        prices.premium === null
    ) {
        console.error("Cannot calculate total: Prices not fully loaded.");
        return { total: 0, discounted: 0 }; // Return zero if prices aren't ready
    }

    const total =
        counts.basic * prices.basic +
        counts.growth * prices.growth +
        counts.pro * prices.pro +
        counts.premium * prices.premium;

    const discounted = total * ((100 - discount) / 100);
    return { total, discounted };
};


const LicenseBuyingPage = () => {
  // **** I18NEXT HOOK ****
  const { t } = useTranslation('licenseBuyingPage'); // Use 'licenseBuyingPage' namespace
  // ***********************

  // State for license counts
  const [basicLicenses, setBasicLicenses] = useState(0);
  const [growthLicenses, setGrowthLicenses] = useState(0); // New state
  const [proLicenses, setProLicenses] = useState(0); // Keep 'pro' identifier
  const [premiumLicenses, setPremiumLicenses] = useState(0); // New state

  // State for prices per license
  const [basicPricePerLicense, setBasicPricePerLicense] = useState(null);
  const [growthPricePerLicense, setGrowthPricePerLicense] = useState(null); // New state
  const [proPricePerLicense, setProPricePerLicense] = useState(null);
  const [premiumPricePerLicense, setPremiumPricePerLicense] = useState(null); // New state

  const [currency, setCurrency] = useState("USD"); // Default currency
  const [discount, setDiscount] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [message, setMessage] = useState(""); // For user feedback messages
  const companyData = useSelector(selectAllCompanys);
  const { currentUser } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState(null); // 'pending', 'completed', 'failed', null
  const [paymentFailureMessage, setPaymentFailureMessage] = useState({}); // Keep for detailed failure context if needed
  const [totalAmount, setTotalAmount] = useState(0);
  const [discountedTotalAmount, setDiscountedTotalAmount] = useState(0);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false); // General loading state
  const [error, setError] = useState(null); // Error message state

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Analytics Event Effect (Add Growth and Premium items)
  useEffect(() => {
    // **** TRANSLATED TITLE ****
    document.title = t('pageTitle'); // Use translated title
    // **************************

    // Log initial view_cart event (quantities are 0 initially)
    logEvent(analytics, "view_cart", {
      currency: currency || "USD", // Use state currency
      value: 0.0,
      items: [
        { item_id: "PRORECRUIT_LICENSE_BASIC", item_name: t('licenseNames.basic'), price: basicPricePerLicense ?? 0, quantity: 0, item_category4: "Basic", /* other params */ },
        { item_id: "PRORECRUIT_LICENSE_GROWTH", item_name: t('licenseNames.growth'), price: growthPricePerLicense ?? 0, quantity: 0, item_category4: "Growth", /* other params */ },
        { item_id: "PRORECRUIT_LICENSE_PRO", item_name: t('licenseNames.professional'), price: proPricePerLicense ?? 0, quantity: 0, item_category4: "Professional", /* other params */ },
        { item_id: "PRORECRUIT_LICENSE_PREMIUM", item_name: t('licenseNames.premium'), price: premiumPricePerLicense ?? 0, quantity: 0, item_category4: "Premium", /* other params */ },
      ].map((item, index) => ({ ...item, index: index + 1, affiliation: "ProRecruit", item_brand: "ProRecruit", item_category: "Software License", item_list_id: "checkout_page", item_list_name: "License Options" })),
    });

    return () => {
      document.title = "ProRecruit"; // Reset title
    };
  // Add 't' to dependency array
  }, [t, currency, basicPricePerLicense, growthPricePerLicense, proPricePerLicense, premiumPricePerLicense]);

  // Fetch Company Data Effect
  useEffect(() => {
    const fetchData = async () => {
      if (currentUser) {
        setUserId(analytics, currentUser.uid);
        setUserProperties(analytics, {
          fullName: currentUser.displayName || "unknown",
          email: currentUser.email || "unknown",
        });
        try {
            const u = await dispatch(fetchUserByEmail(currentUser.email));
            if (u.payload && u.payload.tenantId) {
              const c = await dispatch(fetchAndSetCompanyData(u.payload.tenantId));
              if(c.payload && c.payload.length > 0) {
                setCompany(c.payload[0]);
                setDiscount(c.payload[0].discountPercent || 0);
              } else {
                 // **** TRANSLATED ERROR ****
                 setError(t('errors.fetchCompanyTenantFailed'));
                 // **************************
              }
            } else {
                // **** TRANSLATED ERROR ****
                setError(t('errors.fetchUserTenantFailed'));
                // **************************
            }
        } catch (fetchError) {
            console.error("Error fetching initial data:", fetchError);
            // **** TRANSLATED ERROR ****
            setError(t('errors.loadCompanyInfoFailed'));
            // **************************
            if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
                Sentry.captureException(fetchError);
            }
        }
      }
    };
    fetchData();
  // Add 't' to dependency array
  }, [currentUser, dispatch, t]);

  // Fetch Prices Effect (Updated for all 4 types)
  useEffect(() => {
    const fetchPrices = async () => {
      setLoading(true);
      setError(null);
      setBasicPricePerLicense(null); // Reset prices on company change
      setGrowthPricePerLicense(null);
      setProPricePerLicense(null);
      setPremiumPricePerLicense(null);

      if (!company || !company.tenantId) {
        setLoading(false);
        return;
      }

      try {
        const companyCurrency = company?.country?.currency?.toUpperCase() || "USD";
        setCurrency(companyCurrency);

        const fetchAndSetPrice = async (licenseType) => {
            const priceDocRef = doc(db, "price", licenseType);
            const priceDocSnap = await getDoc(priceDocRef);
            if (priceDocSnap.exists()) {
                const priceData = priceDocSnap.data();
                const lowerCaseCurrency = companyCurrency.toLowerCase();
                if (priceData.price && typeof priceData.price[lowerCaseCurrency] === "number") {
                    return priceData.price[lowerCaseCurrency];
                } else if (priceData.price && typeof priceData.price.usd === "number") {
                    console.warn(`Currency ${companyCurrency} not found for ${licenseType}, defaulting to USD.`);
                    return priceData.price.usd;
                } else {
                    // **** TRANSLATED ERROR (used internally for error message) ****
                    throw new Error(t('errors.invalidPriceStructure', { type: licenseType, currency: companyCurrency }));
                    // *****************************************************************
                }
            } else {
                 // **** TRANSLATED ERROR (used internally for error message) ****
                 throw new Error(t('errors.priceDocumentNotFound', { type: licenseType }));
                 // **************************************************************
            }
        };

        const [basicPrice, growthPrice, proPrice, premiumPrice] = await Promise.all([
            fetchAndSetPrice("basic"),
            fetchAndSetPrice("growth"),
            fetchAndSetPrice("pro"),
            fetchAndSetPrice("premium")
        ]);
        setBasicPricePerLicense(basicPrice);
        setGrowthPricePerLicense(growthPrice);
        setProPricePerLicense(proPrice);
        setPremiumPricePerLicense(premiumPrice);

      } catch (error) {
        console.error("Error fetching prices:", error);
         // **** TRANSLATED ERROR ****
         setError(`${t('errors.loadPricesFailed')}: ${error.message}`);
         // **************************
        if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
          Sentry.captureException(error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
  // Add 't' to dependency array
  }, [company, t]);

  // Calculate Total Amount Effect (No translation needed here)
  useEffect(() => {
    const counts = { basic: basicLicenses, growth: growthLicenses, pro: proLicenses, premium: premiumLicenses };
    const prices = { basic: basicPricePerLicense, growth: growthPricePerLicense, pro: proPricePerLicense, premium: premiumPricePerLicense };
    if (Object.values(prices).every(price => price !== null)) {
      const { total, discounted } = calculateDiscountedTotal(counts, prices, discount);
      setTotalAmount(total);
      setDiscountedTotalAmount(discounted);
    } else {
      setTotalAmount(0);
      setDiscountedTotalAmount(0);
    }
  }, [basicLicenses, growthLicenses, proLicenses, premiumLicenses, basicPricePerLicense, growthPricePerLicense, proPricePerLicense, premiumPricePerLicense, discount]);

  // Payment Status Effect -> Use message state instead of alert
  useEffect(() => {
    if (paymentStatus === "completed") {
       // **** TRANSLATED MESSAGE ****
       setMessage(t('alerts.paymentSuccess'));
       // **************************
       // Maybe add a success icon/state to UI
    } else if (paymentStatus === "failed") {
       // **** TRANSLATED MESSAGE ****
       setMessage(t('alerts.paymentFailed', { error: paymentFailureMessage.message || t('errors.unknown') }));
       // **************************
       // Maybe add a failure icon/state to UI
    }
  }, [paymentStatus, paymentFailureMessage, t]); // Add t

  // Query String Params Effect -> Use message state instead of alert
   useEffect(() => {
     const params = getQueryStringParams();
     if (params.token) {
        // **** TRANSLATED MESSAGE ****
        setMessage(t('alerts.paymentSuccessWithToken', { token: params.token }));
        // **************************
        setPaymentStatus("completed"); // Update status
     }
     // Removed empty dependency array to avoid potential issues if getQueryStringParams changes
   }, [t]); // Add t


  // Stripe Fetch Client Secret (Updated for 4 types)
  const fetchClientSecret = useCallback(async () => {
    setError(null);
    setLoading(true);

    // Validate required data
    if (!companyData || companyData.length === 0 || !companyData[0]?.country?.currency) {
       // **** TRANSLATED ERROR ****
       setError(t('errors.companyDataNotReady'));
       // **************************
       setLoading(false);
       return null;
    }
     if (basicPricePerLicense === null || growthPricePerLicense === null || proPricePerLicense === null || premiumPricePerLicense === null) {
       // **** TRANSLATED ERROR ****
       setError(t('errors.pricingInfoNotReady'));
       // **************************
       setLoading(false);
       return null;
    }
     const totalLicenses = basicLicenses + growthLicenses + proLicenses + premiumLicenses;
      if (totalLicenses <= 0) {
         // **** TRANSLATED ERROR ****
         setError(t('errors.noLicenseSelected'));
         // **************************
         setLoading(false);
         return null;
      }

    try {
        const currentPrices = { basic: basicPricePerLicense, growth: growthPricePerLicense, pro: proPricePerLicense, premium: premiumPricePerLicense };
        const currentCounts = { basic: basicLicenses, growth: growthLicenses, pro: proLicenses, premium: premiumLicenses };
        const { total } = calculateDiscountedTotal(currentCounts, currentPrices, 0);
        // eslint-disable-next-line no-unused-vars
        const { discounted: discountedValue } = calculateDiscountedTotal(currentCounts, currentPrices, discount);

        // Log event with translated names
        logEvent(analytics, "begin_checkout", {
          currency: currency,
          coupon: discount > 0 ? `DISCOUNT_${discount}` : "",
          value: total,
          items: [
            ...(basicLicenses > 0 ? [{ item_id: "PRORECRUIT_LICENSE_BASIC", item_name: t('licenseNames.basic'), price: basicPricePerLicense, quantity: basicLicenses, discount: (basicPricePerLicense * basicLicenses * (discount / 100)), item_category4: "Basic" }] : []),
            ...(growthLicenses > 0 ? [{ item_id: "PRORECRUIT_LICENSE_GROWTH", item_name: t('licenseNames.growth'), price: growthPricePerLicense, quantity: growthLicenses, discount: (growthPricePerLicense * growthLicenses * (discount / 100)), item_category4: "Growth" }] : []),
            ...(proLicenses > 0 ? [{ item_id: "PRORECRUIT_LICENSE_PRO", item_name: t('licenseNames.professional'), price: proPricePerLicense, quantity: proLicenses, discount: (proPricePerLicense * proLicenses * (discount / 100)), item_category4: "Professional" }] : []),
            ...(premiumLicenses > 0 ? [{ item_id: "PRORECRUIT_LICENSE_PREMIUM", item_name: t('licenseNames.premium'), price: premiumPricePerLicense, quantity: premiumLicenses, discount: (premiumPricePerLicense * premiumLicenses * (discount / 100)), item_category4: "Premium" }] : []),
          ].map((item, index) => ({ 
            ...item,
            index: index + 1,
            affiliation: "ProRecruit", // Add other common params
            item_brand: "ProRecruit",
            item_category: "Software License",
            item_list_id: "checkout_page",
            item_list_name: "License Options",
          })),
        });

      const response = await fetch(`/api/StripeCheckoutSession`, { 
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentMethodType: "card",
          companyID: companyData[0].tenantId,
          currency: currency,
          url: `${process.env.REACT_APP_BASE_URL}stripe_payment_return?session_id={CHECKOUT_SESSION_ID}`,
          userID: currentUser.email,
          // License Counts
          basicLicenses,
          growthLicenses, // Add new count
          proLicenses,
          premiumLicenses, // Add new count
          // Prices (send prices used for calculation to backend for verification)
          basicPrice: basicPricePerLicense,
          growthPrice: growthPricePerLicense, // Add new price
          proPrice: proPricePerLicense,
          premiumPrice: premiumPricePerLicense, // Add new price
          discountApplied: discount, // Send discount percentage
        }), }); // Keep fetch as is
      const data = await response.json();

      if (!response.ok || !data.clientSecret) {
        // **** TRANSLATED ERROR ****
        throw new Error(data.error?.message || t('errors.createStripeSessionFailed'));
        // **************************
      }

      setLoading(false);
      return data.clientSecret;

    } catch (error) {
      console.error("Error fetching client secret:", error.message);
      // **** TRANSLATED ERROR ****
      setError(`${t('errors.initializePaymentFailed')}: ${error.message}`);
      // **************************
       if (process.env.REACT_APP_ENVIRONEMENT === "prod") { Sentry.captureException(error); }
      setLoading(false);
      return null;
    }
  // Add 't' to dependency array
  }, [
      t, companyData, currency, discount, currentUser, // Keep existing
      basicLicenses, growthLicenses, proLicenses, premiumLicenses,
      basicPricePerLicense, growthPricePerLicense, proPricePerLicense, premiumPricePerLicense
  ]);

  const stripeSessionOptions = { options: { fetchClientSecret } };

  const getQueryStringParams = () => { 
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return {
      token: urlParams.get("token"),
      payment: urlParams.get("payment"),
    };
   };

   // Input Handlers (Add for Growth and Premium)
   const handleBasicChange = (event) => {
    const count = parseInt(event.target.value);
    setBasicLicenses(isNaN(count) || count < 0 ? 0 : count);
  };
  const handleGrowthChange = (event) => { // New handler
    const count = parseInt(event.target.value);
    setGrowthLicenses(isNaN(count) || count < 0 ? 0 : count);
  };
  const handleProChange = (event) => {
    const count = parseInt(event.target.value);
    setProLicenses(isNaN(count) || count < 0 ? 0 : count);
  };
   const handlePremiumChange = (event) => { // New handler
    const count = parseInt(event.target.value);
    setPremiumLicenses(isNaN(count) || count < 0 ? 0 : count);
  };


  const handlePaymentChange = (paymentMethod) => {
    setSelectedPayment(paymentMethod);
     setError(null); // Clear errors when changing payment method
  };

  // Generic Payment Success Handler (Pass all license counts)
  const handlePaymentSuccess = async (transactionId, transactionType) => {
    setPaymentStatus("pending");
    setLoading(true);
    setMessage(""); // Clear previous messages
    setError(null); // Clear previous errors

    const prices = { /* ... */ };
    if (Object.values(prices).some(price => price === null)) {
        console.error("Cannot process payment: Prices not loaded.");
        // **** TRANSLATED ERROR ****
        setError(t('errors.processPaymentPriceError'));
        // **************************
        setPaymentStatus("failed");
        setPaymentFailureMessage({ status: "failed", message: t('errors.processPaymentPriceError') });
        setLoading(false);
        return;
     }

     const counts = { basic: basicLicenses, growth: growthLicenses, pro: proLicenses, premium: premiumLicenses };
     const { discounted: finalAmount } = calculateDiscountedTotal(counts, prices, discount);

    const functions = getFunctions();
    const processPayment = httpsCallable(functions, "processSuccessfulPayment");

    try {
      const result = await processPayment({
        companyID: companyData[0].tenantId,
        userID: currentUser.email,
        timestamp: Date.now(),
        totalAmount: finalAmount, // Use the calculated discounted amount
        transactionId: transactionId,
        transactionType: transactionType,
        currency: currency, // Pass currency
        discountApplied: discount, // Pass discount
        country: companyData[0]?.country?.name || "",
        city: companyData[0]?.city || "",
        name: companyData[0]?.name || "",
        // Pass all license quantities
        basicLicenses: basicLicenses,
        growthLicenses: growthLicenses, // Add
        proLicenses: proLicenses,
        premiumLicenses: premiumLicenses, // Add
      });

      console.log("Payment processed successfully via Cloud Function:", result.data);
      setPaymentStatus("completed");
      // **** TRANSLATED MESSAGE ****
      setMessage(t('alerts.paymentProcessSuccess')); // More specific success message
      // **************************
      navigate("/");

    } catch (error) {
        console.error("Cloud Function payment processing error:", error);
         if (process.env.REACT_APP_ENVIRONEMENT === "prod") { Sentry.captureException(error); }
        setPaymentFailureMessage({
            status: "failed",
            // **** TRANSLATED ERROR ****
            message: error.message || t('errors.licenseUpdateFailed'),
            // **************************
        });
        setPaymentStatus("failed");
        // Log error to Firestore (include all license counts)
        try {
          await addDoc(collection(db, "prorecruit_error"), {
          page: "LicenseBuyingPage",
          action: "handlePaymentSuccess",
          data: {
              companyName: company?.name || "N/A",
              tenantId: company?.tenantId || "N/A",
              country: company?.country?.name || "N/A",
              transactionId: transactionId,
              transactionType: transactionType,
              finalAmount: finalAmount,
              basicLicenses: basicLicenses,
              growthLicenses: growthLicenses, // Add
              proLicenses: proLicenses,
              premiumLicenses: premiumLicenses, // Add
          },
          message: error.message || "Unknown error",
          stack: error.stack || "No stack trace",
          name: error.name || "Error",
          timestamp: Timestamp.now(),
          userId: currentUser?.uid || 'unknown',
          userEmail: currentUser?.email || 'unknown',
          });
      } catch (firestoreError) {
          console.error("Error saving error log to Firestore:", firestoreError);
          if (process.env.REACT_APP_ENVIRONMENT === "prod") {
              Sentry.captureException(firestoreError);
          }
      }
    } finally {
        setLoading(false);
    }
  };

 // CinetPay Success Handler (Calls generic handler)
 const handleCinetpaySuccess = async (response, transactionId) => {
  if (response.status === "ACCEPTED") {
      console.log("CinetPay payment accepted, processing...", transactionId);
      await handlePaymentSuccess(transactionId, "CinetPay"); // Call generic handler
  } else {
       // Handle non-ACCEPTED but potentially successful states if CinetPay has them
       console.warn("CinetPay status not ACCEPTED:", response.status, transactionId);
       handleCinetpayFailure(response, transactionId); // Treat as failure if not explicitly accepted
  }
};

  // CinetPay Failure Handler -> Use message state
  const handleCinetpayFailure = (error, transactionId) => {
     console.error("CinetPay Payment Failure:", error, transactionId);
     let userMessage = t('errors.cinetpayUnexpected'); // Default translated message
     if (error?.message) {
         userMessage = t('errors.cinetpayWithMessage', { error: error.message });
     }
     if (error?.status === "REFUSED" || error?.code === "TRANSACTION_DECLINED") {
         userMessage = t('errors.cinetpayDeclined');
     } else if (error?.code === "INSUFFICIENT_FUNDS") {
         userMessage = t('errors.cinetpayInsufficientFunds');
     }
     setPaymentFailureMessage({ status: "failed", message: userMessage }); // Keep detailed message if needed
     setMessage(userMessage); // Set user-facing message
     setPaymentStatus("failed");
  };

  // PayPal `createOrder` -> Use message state for errors
  const createPayPalOrder = async (data, actions) => {
       setError(null);
       setLoading(true);
       const prices = { basic: basicPricePerLicense, growth: growthPricePerLicense, pro: proPricePerLicense, premium: premiumPricePerLicense };
       if (Object.values(prices).some(price => price === null)) {
             const errorMsg = t('errors.paypalPriceError'); // Translated
             console.error(errorMsg);
             setError(errorMsg);
             setMessage(errorMsg);
             setLoading(false);
             return Promise.reject(new Error(errorMsg));
         }
        try {
            // Calculate amounts in USD for PayPal
            const calculateUSDAmount = async (count, pricePerLicenseLocal) => {
              if (count <= 0 || pricePerLicenseLocal === null) return 0;
               const localTotal = count * pricePerLicenseLocal;
               const discountedLocal = localTotal * ((100 - discount) / 100);
              if (currency === "USD") {
                   return parseFloat(discountedLocal.toFixed(2));
               } else {
                  // Convert the discounted amount per license type
                  const converted = await convertCurrencyWithFee(discountedLocal, currency, "USD");
                   return parseFloat(converted.toFixed(2));
               }
          };
           // Calculate individual converted amounts (ensure error handling in convertCurrencyWithFee)
          const [usdBasicAmount, usdGrowthAmount, usdProAmount, usdPremiumAmount] = await Promise.all([
              calculateUSDAmount(basicLicenses, basicPricePerLicense),
              calculateUSDAmount(growthLicenses, growthPricePerLicense),
              calculateUSDAmount(proLicenses, proPricePerLicense),
              calculateUSDAmount(premiumLicenses, premiumPricePerLicense)
          ]);

          // Calculate unit prices in USD (for item breakdown) - Convert the *original* price per license
           const getUnitUSDPrice = async (priceLocal) => {
              if (priceLocal === null) return '0.00'; // Should not happen if initial check passes
               if (currency === "USD") return priceLocal.toFixed(2);
               const converted = await convertCurrencyWithFee(priceLocal, currency, "USD");
               return converted.toFixed(2); // Return as string for PayPal API
           };
          const [usdUnitBasic, usdUnitGrowth, usdUnitPro, usdUnitPremium] = await Promise.all([
              getUnitUSDPrice(basicPricePerLicense),
              getUnitUSDPrice(growthPricePerLicense),
              getUnitUSDPrice(proPricePerLicense),
              getUnitUSDPrice(premiumPricePerLicense)
          ]);

          // Calculate total USD amount after individual conversions
          const totalUSDAmount = parseFloat(
              (usdBasicAmount + usdGrowthAmount + usdProAmount + usdPremiumAmount).toFixed(2)
          );

             if (totalUSDAmount <= 0) {
                 const errorMsg = t('errors.paypalAmountError'); // Translated
                 console.error(errorMsg);
                 setError(errorMsg);
                 setMessage(errorMsg);
                 setLoading(false);
                 return Promise.reject(new Error(errorMsg));
             }
             // Generate a unique invoice ID
            const timestamp = Date.now().toString(36);
            const randomString = Math.random().toString(36).substring(2, 10).toUpperCase();
            const invoiceID = `PRLPP-${timestamp}-${randomString}`;

            // Build the items array for PayPal
            const items = [];
            if (basicLicenses > 0) items.push({ name: "ProRecruit Basic License", description: `Basic License x ${basicLicenses}`, unit_amount: { currency_code: "USD", value: usdUnitBasic }, quantity: `${basicLicenses}`, category: "DIGITAL_GOODS", sku: "PR-BASIC" });
            if (growthLicenses > 0) items.push({ name: "ProRecruit Growth License", description: `Growth License x ${growthLicenses}`, unit_amount: { currency_code: "USD", value: usdUnitGrowth }, quantity: `${growthLicenses}`, category: "DIGITAL_GOODS", sku: "PR-GROWTH" });
            if (proLicenses > 0) items.push({ name: "ProRecruit Pro License", description: `Pro License x ${proLicenses}`, unit_amount: { currency_code: "USD", value: usdUnitPro }, quantity: `${proLicenses}`, category: "DIGITAL_GOODS", sku: "PR-PRO" });
            if (premiumLicenses > 0) items.push({ name: "ProRecruit Premium License", description: `Premium License x ${premiumLicenses}`, unit_amount: { currency_code: "USD", value: usdUnitPremium }, quantity: `${premiumLicenses}`, category: "DIGITAL_GOODS", sku: "PR-PREMIUM" });

            // Calculate item_total based on unit prices and quantities (before discount)
             const itemTotalValue = items.reduce((sum, item) => sum + (parseFloat(item.unit_amount.value) * parseInt(item.quantity)), 0).toFixed(2);
            // Calculate discount amount (difference between item total and final amount)
             const discountValue = (parseFloat(itemTotalValue) - totalUSDAmount).toFixed(2);


            console.log("Creating PayPal order with amount:", totalUSDAmount, "USD", "Invoice:", invoiceID);
            console.log("Items:", items);
            console.log("Item Total:", itemTotalValue, "Discount:", discountValue);


            setLoading(false); // Order creation setup is done
            return actions.order.create({
                purchase_units: [{
                    invoice_id: invoiceID,
                    amount: {
                        currency_code: "USD",
                        value: `${totalUSDAmount}`, // Final amount after discount
                        breakdown: {
                            item_total: { currency_code: "USD", value: `${itemTotalValue}` }, // Sum of (unit_price * quantity) before discount
                            discount: { currency_code: "USD", value: `${discountValue}` } // Apply discount here
                            // tax_total, shipping, handling etc. if applicable
                        }
                    },
                    items: items,
                }],
            });
        } catch (error) {
            console.error("Error preparing PayPal order:", error);
            const errorMsg = t('errors.paypalCreateFailed', { error: error.message }); // Translated
            setError(errorMsg);
            setMessage(errorMsg);
            if (process.env.REACT_APP_ENVIRONMENT === "prod") { Sentry.captureException(error); }
            try { await addDoc(collection(db, "prorecruit_error"), { /* ... */ }); } catch (firestoreError) { /* ... */ }
            setLoading(false);
             return Promise.reject(error);
        }
  };

  // PayPal `onApprove` -> Use message state
  const onPayPalApprove = async (data, actions) => {
        console.log("PayPal payment approved:", data);
        setPaymentStatus("pending");
        setLoading(true);
        setMessage(""); // Clear message
        setError(null); // Clear error
        return actions.order.capture().then(async (details) => {
            console.log("PayPal order captured:", details);
            // **** TRANSLATED MESSAGE ****
            setMessage(t('alerts.paypalCompletedBy', { name: details.payer?.name?.given_name || t('payer') }));
            // **************************
            await handlePaymentSuccess(details.id, "PayPal");
             setLoading(false);
        }).catch(err => {
             console.error("Error capturing PayPal order:", err);
             const errorMsg = t('errors.paypalCaptureFailed', { error: err.message }); // Translated
             setError(errorMsg);
             setMessage(errorMsg);
             setPaymentFailureMessage({ status: 'failed', message: errorMsg });
             setPaymentStatus('failed');
              if (process.env.REACT_APP_ENVIRONMENT === "prod") { Sentry.captureException(err); }
             try { addDoc(collection(db, "prorecruit_error"), { /* ... */ }); } catch (firestoreError) { /* ... */ }
             setLoading(false);
        });
  };

  // PayPal `onError` -> Use message state
  const onPayPalError = (err) => {
       console.error("PayPal Checkout onError:", err);
       const errorMsg = t('errors.paypalOnError', { error: err.message || t('errors.unknown') }); // Translated
       setError(errorMsg);
       setPaymentFailureMessage({ status: "failed", message: errorMsg});
       setMessage(errorMsg); // Set user-facing message
       setPaymentStatus("failed");
        if (process.env.REACT_APP_ENVIRONMENT === "prod") { Sentry.captureException(err); }
        setLoading(false);
  };

  // Calculate total licenses for disabling payment buttons
  const totalLicensesSelected = basicLicenses + growthLicenses + proLicenses + premiumLicenses;


  return (
    <>
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* **** TRANSLATED HEADING **** */}
        <h2 className="text-3xl font-bold mb-6 text-center text-slate-900 font-bold tracking-tight">{t('mainHeading')}</h2>
        {/* ************************** */}

         {/* Display Loading Indicator */}
         {loading && (
           <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
             <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white"></div>
             {/* **** TRANSLATED TEXT **** */}
             <span className="ml-4 text-white text-xl">{t('loadingText')}</span>
             {/* ************************** */}
           </div>
         )}

         {/* Display General Errors */}
         {error && (
           <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
             {/* **** TRANSLATED TEXT **** */}
             <strong className="font-bold">{t('errorPrefix')}</strong>
             {/* ************************** */}
             <span className="block sm:inline">{error}</span>
           </div>
         )}
         {/* Display Status Messages */}
          {message && !error && ( // Only show message if no overriding error
            <div className={`p-4 mb-4 text-sm rounded-lg ${
                paymentStatus === 'completed' ? 'bg-green-100 text-green-700' :
                paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                paymentStatus === 'failed' ? 'bg-red-100 text-red-700' : // Use message state for failed now
                'bg-blue-100 text-blue-700' // Default info message
                }`} role="status">
                {message}
            </div>
          )}


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Basic License Input */}
          <div>
            {/* **** TRANSLATED LABEL **** */}
            <label htmlFor="basic-licenses" className="block text-slate-600 leading-relaxed font-bold mb-2">
              {t('labels.basic')}:
            </label>
            {/* ************************** */}
            <div className="relative">
              <input id="basic-licenses" type="number" value={basicLicenses} onChange={handleBasicChange} min={0} className="..." aria-describedby="basic-price-desc"/>
              {basicPricePerLicense !== null && (
                 <span id="basic-price-desc" className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs leading-5 text-slate-500 pointer-events-none">
                    {/* **** TRANSLATED ANNOTATION **** */}
                    {basicPricePerLicense} {currency}/{t('perLicense')}
                    {/* ************************** */}
                 </span>
              )}
            </div>
          </div>

          {/* Growth License Input */}
          <div>
             {/* **** TRANSLATED LABEL **** */}
             <label htmlFor="growth-licenses" className="block text-slate-600 leading-relaxed font-bold mb-2">
               {t('labels.growth')}:
             </label>
             {/* ************************** */}
             <div className="relative">
              <input id="growth-licenses" type="number" value={growthLicenses} onChange={handleGrowthChange} min={0} className="..." aria-describedby="growth-price-desc"/>
              {growthPricePerLicense !== null && (
                 <span id="growth-price-desc" className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs leading-5 text-slate-500 pointer-events-none">
                   {/* **** TRANSLATED ANNOTATION **** */}
                   {growthPricePerLicense} {currency}/{t('perLicense')}
                   {/* ************************** */}
                 </span>
              )}
            </div>
          </div>

          {/* Pro License Input */}
          <div>
             {/* **** TRANSLATED LABEL **** */}
             <label htmlFor="pro-licenses" className="block text-slate-600 leading-relaxed font-bold mb-2">
                {t('labels.professional')}:
             </label>
             {/* ************************** */}
             <div className="relative">
              <input id="pro-licenses" type="number" value={proLicenses} onChange={handleProChange} min={0} className="..." aria-describedby="pro-price-desc"/>
              {proPricePerLicense !== null && (
                 <span id="pro-price-desc" className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs leading-5 text-slate-500 pointer-events-none">
                   {/* **** TRANSLATED ANNOTATION **** */}
                   {proPricePerLicense} {currency}/{t('perLicense')}
                   {/* ************************** */}
                 </span>
              )}
            </div>
          </div>

          {/* Premium License Input */}
          <div>
             {/* **** TRANSLATED LABEL **** */}
             <label htmlFor="premium-licenses" className="block text-slate-600 leading-relaxed font-bold mb-2">
               {t('labels.premium')}:
             </label>
             {/* ************************** */}
             <div className="relative">
              <input id="premium-licenses" type="number" value={premiumLicenses} onChange={handlePremiumChange} min={0} className="..." aria-describedby="premium-price-desc"/>
              {premiumPricePerLicense !== null && (
                <span id="premium-price-desc" className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs leading-5 text-slate-500 pointer-events-none">
                   {/* **** TRANSLATED ANNOTATION **** */}
                   {premiumPricePerLicense} {currency}/{t('perLicense')}
                   {/* ************************** */}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Total Amount Display */}
         <div className="text-center mt-6 mb-8 p-4 bg-gray-100 rounded-lg shadow">
             <h3 className="text-xl font-semibold text-slate-900 font-bold tracking-tight">{t('totalAmountHeading')}</h3>
             <div className="flex items-center justify-center space-x-4 mt-2">
                 {discount > 0 && totalAmount > 0 && ( 
                    <span className="text-slate-500 line-through text-xl">
                         {totalAmount.toFixed(2)} {currency}
                     </span>
                 )}
                <span className="text-green-600 text-3xl font-extrabold">{discountedTotalAmount.toFixed(2)} {currency}</span>
                 {discount > 0 && discountedTotalAmount > 0 && (
                    <span className="text-white bg-red-500 px-3 py-1 rounded-full text-sm font-medium shadow">
                       -{discount}{t('discountOffSuffix')}
                    </span>
                 )}
             </div>
             {totalAmount === 0 && discountedTotalAmount === 0 && totalLicensesSelected > 0 && !loading && (
                 <p className="text-sm text-slate-500 mt-2">{t('calculatingTotal')}</p>
             )}
              {totalLicensesSelected <= 0 && (
                 <p className="text-sm text-slate-500 mt-2">{t('selectLicensesPrompt')}</p>
             )}
         </div>


        {/* Payment Method Selection */}
        <div className="mt-6">
          {/* **** TRANSLATED HEADING **** */}
          <h4 className="text-lg font-bold mb-4 text-center text-slate-600 leading-relaxed">
            {t('choosePaymentMethod')}
          </h4>
          {/* ************************** */}
          <div className="flex flex-wrap gap-4 justify-center mb-6">
            {/* CinetPay Button */}
            <button
              className={`... ${totalLicensesSelected < 1 || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={totalLicensesSelected < 1 || loading}
              onClick={() => handlePaymentChange("cinetpay")}
              title={t('paymentTooltips.cinetpay')} // Translated tooltip
            >
              {t('paymentMethods.cinetpay')} {/* Translated text */}
            </button>
            {/* Stripe Button */}
            <button
              className={`... ${totalLicensesSelected < 1 || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={totalLicensesSelected < 1 || loading}
              onClick={() => handlePaymentChange("card")}
              title={t('paymentTooltips.stripe')} // Translated tooltip
            >
              {t('paymentMethods.stripe')} {/* Translated text */}
            </button>
             {/* PayPal Button */}
            <button
              className={`... ${totalLicensesSelected < 1 || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={totalLicensesSelected < 1 || loading}
              onClick={() => handlePaymentChange("paypal")}
              title={t('paymentTooltips.paypal')} // Translated tooltip
            >
              {t('paymentMethods.paypal')} {/* Translated text */}
            </button>
          </div>

           {/* Conditional Rendering for Payment Components */}
            <div className="mt-6 p-6 border border-gray-200 rounded-lg bg-white shadow-md min-h-[200px] flex items-center justify-center">
                {!selectedPayment && totalLicensesSelected > 0 && (
                    <p className="text-slate-500">{t('selectPaymentPrompt')}</p>
                )}
                 {!selectedPayment && totalLicensesSelected <= 0 && (
                    <p className="text-slate-500">{t('selectLicensesForPayment')}</p>
                )}
                {/* Stripe */}
                {selectedPayment === "card" && totalLicensesSelected > 0 && ( 
                    <div id="checkout" className="w-full">
                        <EmbeddedCheckoutProvider
                            options={stripeSessionOptions.options}
                            stripe={stripePromise}
                            key={selectedPayment} // Add key to force re-render on change
                        >
                            <EmbeddedCheckout className="w-full" />
                        </EmbeddedCheckoutProvider>
                     </div>
                )}
                {/* CinetPay */}
                {selectedPayment === "cinetpay" && totalLicensesSelected > 0 && ( <CinetPayButton description={t('cinetpayDescription')} 
                            amount={discountedTotalAmount}
                            // CinetPay might require specific currency, adjust if needed
                            currency={currency === 'XAF' || currency === 'XOF' ? currency : 'XOF'} // Default or use specific allowed CinetPay currency
                            onPaymentSuccess={handleCinetpaySuccess}
                            onPaymentFailure={handleCinetpayFailure}
                            // Pass additional metadata if needed by CinetPay
                            // metadata={{ userId: currentUser?.uid, companyId: company?.tenantId }}
                         /> )}
                {/* PayPal */}
                {selectedPayment === "paypal" && totalLicensesSelected > 0 && ( 
                     <div className="w-full max-w-md mx-auto"> {/* Constrain PayPal button width */}
                        <PayPalScriptProvider
                            options={{
                            "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || "AfCRB2-04sY9JsO2WP3tDIx-XDDOiZ6l-FIaVoAOa25uaX3NbmVEmp3ysCGTpuVlWAAwrQBeDPPFroTj", // Use env variable
                            components: "buttons",
                            currency: "USD", // PayPal standard button currency
                            intent: "capture", // Explicitly set intent
                            }}
                            key={selectedPayment} // Add key to force re-render
                        >
                            <PayPalButtons
                                style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                                createOrder={createPayPalOrder}
                                onApprove={onPayPalApprove}
                                onError={onPayPalError}
                                key={discountedTotalAmount} // Force re-render if amount changes significantly
                             />
                        </PayPalScriptProvider>
                    </div>
                 )}
            </div>
        </div>

        <TawkToChat />
      </div>
    </>
  );
};

export default LicenseBuyingPage;